import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Shield, AlertTriangle, TrendingUp, Activity, Moon, 
  Heart, Loader2, ChevronDown, ChevronUp, Info,
  AlertCircle, CheckCircle, XCircle, Zap, Ruler
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const RISK_COLORS = {
  low: { bg: 'bg-green-500/20', border: 'border-green-500/50', text: 'text-green-400', icon: CheckCircle },
  moderate: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', text: 'text-yellow-400', icon: AlertCircle },
  high: { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-400', icon: AlertTriangle },
  critical: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400', icon: XCircle },
  unknown: { bg: 'bg-zinc-500/20', border: 'border-zinc-500/50', text: 'text-zinc-400', icon: Info }
};

const FACTOR_ICONS = {
  'Training Load (ACWR)': Activity,
  'Growth Velocity': TrendingUp,
  'Maturity Status': Ruler,
  'Recovery & Fatigue': Moon
};

export default function InjuryPrevention({ childId, childName }) {
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [expandedFactors, setExpandedFactors] = useState({});

  const fetchAssessment = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/injury-prevention/risk-assessment/${childId}`);
      setAssessment(res.data);
    } catch (err) {
      console.error('Failed to fetch injury risk assessment');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (childId) {
      fetchAssessment();
    }
  }, [childId, fetchAssessment]);

  const toggleFactor = (name) => {
    setExpandedFactors(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-[#02b5ff]" />
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <p className="text-zinc-500 text-center">Unable to load injury risk assessment</p>
      </div>
    );
  }

  const riskStyle = RISK_COLORS[assessment.risk_level] || RISK_COLORS.unknown;
  const RiskIcon = riskStyle.icon;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="injury-prevention">
      {/* Header */}
      <div 
        className="p-4 border-b border-zinc-800 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${riskStyle.bg} flex items-center justify-center`}>
            <Shield className={`h-5 w-5 ${riskStyle.text}`} />
          </div>
          <div>
            <h3 className="font-bold text-white flex items-center gap-2">
              Injury Prevention
              <span className={`text-xs px-2 py-0.5 rounded-full ${riskStyle.bg} ${riskStyle.text} uppercase`}>
                {assessment.risk_level} Risk
              </span>
            </h3>
            <p className="text-xs text-zinc-400">AI-powered injury risk assessment</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Risk Score Circle */}
          <div className="relative w-14 h-14">
            <svg className="w-14 h-14 transform -rotate-90">
              <circle
                cx="28"
                cy="28"
                r="24"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                className="text-zinc-800"
              />
              <circle
                cx="28"
                cy="28"
                r="24"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                strokeDasharray={`${assessment.overall_score * 1.5} 150`}
                className={riskStyle.text}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-lg font-bold ${riskStyle.text}`}>{Math.round(assessment.overall_score)}</span>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-zinc-500" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Alerts */}
          {assessment.alerts && assessment.alerts.length > 0 && (
            <div className="space-y-2">
              {assessment.alerts.map((alert, i) => (
                <div 
                  key={i}
                  className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-3"
                >
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{alert}</p>
                </div>
              ))}
            </div>
          )}

          {/* Risk Factors */}
          <div className="space-y-3">
            {assessment.factors.map((factor, i) => {
              const factorStyle = RISK_COLORS[factor.level] || RISK_COLORS.unknown;
              const FactorIcon = FACTOR_ICONS[factor.name] || Zap;
              const isExpanded = expandedFactors[factor.name];
              
              return (
                <div 
                  key={i}
                  className={`rounded-xl border ${factorStyle.border} overflow-hidden`}
                >
                  {/* Factor Header */}
                  <div 
                    className={`p-4 ${factorStyle.bg} cursor-pointer`}
                    onClick={() => toggleFactor(factor.name)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FactorIcon className={`h-5 w-5 ${factorStyle.text}`} />
                        <div>
                          <h4 className="font-semibold text-white">{factor.name}</h4>
                          <p className="text-xs text-zinc-400">{factor.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className={`text-lg font-bold ${factorStyle.text}`}>{Math.round(factor.score)}</div>
                          <div className={`text-xs ${factorStyle.text} uppercase`}>{factor.level}</div>
                        </div>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-3 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${factorStyle.text.replace('text-', 'bg-')} transition-all duration-500`}
                        style={{ width: `${factor.score}%` }}
                      />
                    </div>
                  </div>

                  {/* Recommendations (Expandable) */}
                  {isExpanded && factor.recommendations.length > 0 && (
                    <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
                      <h5 className="text-xs text-zinc-500 uppercase mb-2">Recommendations</h5>
                      <ul className="space-y-2">
                        {factor.recommendations.map((rec, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-zinc-300">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            {/* ACWR */}
            {assessment.acwr_data?.acwr && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{assessment.acwr_data.acwr}</div>
                <div className="text-xs text-zinc-500">ACWR</div>
                <div className={`text-xs mt-1 ${
                  assessment.acwr_data.status === 'optimal' ? 'text-green-400' :
                  assessment.acwr_data.status === 'elevated' ? 'text-orange-400' :
                  assessment.acwr_data.status === 'danger' ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {assessment.acwr_data.status}
                </div>
              </div>
            )}
            
            {/* Growth Velocity */}
            {assessment.growth_data?.velocity && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{assessment.growth_data.velocity}</div>
                <div className="text-xs text-zinc-500">cm/year</div>
                <div className={`text-xs mt-1 ${
                  assessment.growth_data.status === 'normal' ? 'text-green-400' :
                  assessment.growth_data.status === 'elevated' ? 'text-yellow-400' : 'text-orange-400'
                }`}>
                  growth rate
                </div>
              </div>
            )}
            
            {/* PHV Offset */}
            {assessment.phv_data?.offset !== null && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{assessment.phv_data.offset > 0 ? '+' : ''}{assessment.phv_data.offset}</div>
                <div className="text-xs text-zinc-500">years from PHV</div>
                <div className={`text-xs mt-1 ${
                  assessment.phv_data.injury_risk === 'low' ? 'text-green-400' :
                  assessment.phv_data.injury_risk === 'moderate' ? 'text-yellow-400' :
                  assessment.phv_data.injury_risk === 'elevated' ? 'text-orange-400' : 'text-red-400'
                }`}>
                  {assessment.phv_data.status?.replace('_', ' ')}
                </div>
              </div>
            )}
            
            {/* Recovery */}
            {assessment.recovery_data?.avg_sleep && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{assessment.recovery_data.avg_sleep}h</div>
                <div className="text-xs text-zinc-500">avg sleep</div>
                <div className={`text-xs mt-1 ${
                  assessment.recovery_data.avg_sleep >= 8 ? 'text-green-400' :
                  assessment.recovery_data.avg_sleep >= 7 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {assessment.recovery_data.avg_score}% recovery
                </div>
              </div>
            )}
          </div>

          {/* Last Updated */}
          <p className="text-xs text-zinc-600 text-center">
            Last updated: {new Date(assessment.last_updated).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
